"use client";

import { ArrowRightLeft, Bot, Check, FolderOpen, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { COMMAND_V2_DIALOG_CONTENT_CLASS } from "@/components/command-team-v2/utils/dialog-styles";

export interface CommandV2AgentSwitchPrompt {
    sessionId: string;
    projectId: string | null;
    projectName?: string | null;
    sessionAgentId: string;
    sessionAgentName: string;
    sessionAgentRole?: string | null;
    currentAgentId: string;
    currentAgentName: string;
    currentAgentRole?: string | null;
    sessionTitle: string;
}

interface CommandV2AgentSwitchDialogProps {
    prompt: CommandV2AgentSwitchPrompt | null;
    onClose: () => void;
    onSwitch: () => void;
    onStay: () => void;
}

function AgentOptionCard({
    name,
    role,
    description,
    badge,
    recommended,
    onClick,
}: {
    name: string;
    role?: string | null;
    description: string;
    badge?: string;
    recommended?: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "group flex w-full items-start gap-3 rounded-2xl border px-3.5 py-3 text-left transition-all",
                recommended
                    ? "border-primary/30 bg-violet-50/80 hover:border-primary/50 hover:bg-violet-50 dark:bg-violet-950/20 dark:hover:bg-violet-950/35"
                    : "border-border bg-card hover:border-primary/25 hover:bg-muted/40"
            )}
        >
            <span
                className={cn(
                    "flex size-9 shrink-0 items-center justify-center rounded-[9px]",
                    recommended
                        ? "bg-violet-100 dark:bg-violet-900/40"
                        : "bg-muted"
                )}
            >
                <Bot
                    className={cn(
                        "size-4",
                        recommended ? "text-primary" : "text-muted-foreground"
                    )}
                />
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-semibold">{name}</span>
                    {badge ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                            <Sparkles className="size-2.5" />
                            {badge}
                        </span>
                    ) : null}
                </span>
                {role ? (
                    <span className="mt-0.5 block truncate text-[11px] font-medium text-primary/80">
                        {role}
                    </span>
                ) : null}
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                    {description}
                </span>
            </span>
            <ArrowRightLeft
                className={cn(
                    "mt-1 size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-60",
                    recommended ? "text-primary" : "text-muted-foreground"
                )}
            />
        </button>
    );
}

export function CommandV2AgentSwitchDialog({
    prompt,
    onClose,
    onSwitch,
    onStay,
}: CommandV2AgentSwitchDialogProps) {
    const open = prompt !== null;

    return (
        <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
            <DialogContent className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, "max-w-lg")}>
                <div className="flex items-start gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30">
                        <ArrowRightLeft className="size-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1 pr-6">
                        <DialogTitle className="text-left text-lg font-semibold tracking-tight">
                            This chat uses another agent
                        </DialogTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Pick which agent should answer when you open this chat.
                        </p>
                    </div>
                </div>

                {prompt ? (
                    <div className="mt-4 rounded-xl border border-dashed border-border/80 bg-muted/30 px-3.5 py-2.5">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Opening
                        </p>
                        <p className="mt-0.5 truncate text-sm font-medium text-foreground">
                            {prompt.sessionTitle}
                        </p>
                        {prompt.projectName ? (
                            <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                                <FolderOpen className="size-3 shrink-0" />
                                <span className="truncate">{prompt.projectName}</span>
                            </p>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-4 space-y-2">
                    <AgentOptionCard
                        name={prompt?.sessionAgentName ?? ""}
                        role={prompt?.sessionAgentRole}
                        badge="Recommended"
                        recommended
                        description="Same agent, tools, and persona this chat was built with."
                        onClick={onSwitch}
                    />
                    <AgentOptionCard
                        name={prompt?.currentAgentName ?? ""}
                        role={prompt?.currentAgentRole}
                        description="Keep your current agent — useful if you want a fresh perspective."
                        onClick={onStay}
                    />
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-border/60 pt-4">
                    <p className="text-xs text-muted-foreground">
                        <Check className="mr-1 inline size-3" />
                        You can change agents anytime from the composer.
                    </p>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        className="h-8 shrink-0 rounded-full px-3 text-xs font-medium text-muted-foreground"
                    >
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
